import { campaignMetrics, ChartDataResponse, elementWithMin, MetricInfo, MetricLabels } from "@api/common";
import { formatNumber } from "@common";
import { AppState, currentReportCampaign, ReportCampaignListItem } from "@store";
import * as d3 from "d3";
import { FetchingState } from "rd-redux-http";
import * as React from "react";
import { createElement } from "react-faux-dom";
import { connect } from "react-redux";
import { AutoScaleSvg } from "./AutoScaleSvg";
import { LegendItem } from "./LegendItem";
import { ReportLabel } from "./ReportLabel";
import "./ReportMetricsPerDateChart.css";
import { clientCoordsToSvgCoords } from "./svg-utils";

interface ReportMetricsPerDateChartProps {
    chart: ChartDataResponse;
    metrics: MetricInfo[];
}

interface ReportMetricsPerDateChartState {
    isMouseOver: boolean;
    clientX: number;
    clientY: number;
    metric?: string;

    displayedChart: string;
}

const viewportWidth = 500;
const viewportHeight = 100;

const plotWidth = viewportWidth * 0.8;
const plotHeight = viewportHeight * 0.8;

const offsetX = (viewportWidth - plotWidth) / 2;
const offsetY = (viewportHeight - plotHeight) / 2;

const numOfTicks = 5;

class ReportMetricsPerDateChartComponent extends React.Component<
    ReportMetricsPerDateChartProps,
    ReportMetricsPerDateChartState
> {
    private svg: SVGSVGElement | undefined;

    constructor(props: ReportMetricsPerDateChartProps, context?: any) {
        super(props, context);

        this.state = {
            isMouseOver: false,
            clientX: 0,
            clientY: 0,
            displayedChart: props.metrics[0].metric
        };
    }

    render() {
        return (
            <div className="ReportMetricsPerDateChart">
                <div className="head">
                    <ReportLabel title="Over period of time" />
                    {this.renderLegend()}
                </div>
                {this.renderPlot(this.state.displayedChart)}
                <p className="ReportMetricsPerDateChart_disclaimer">
                    Due to delayed attribution all metrics acquired within the last 48 hours are still subject to
                    change.
                </p>
            </div>
        );
    }

    private renderPlot(metric: string) {
        return (
            <AutoScaleSvg
                className="plot"
                viewportWidth={viewportWidth}
                viewportHeight={viewportHeight}
                ref={ref => (this.svg = !!ref ? ref.svg : undefined)}
            >
                <g transform={`translate(${offsetX}, ${offsetY})`}>{this.renderAxis(metric)}</g>
                {this.renderTooltip()}
                <g
                    width={plotWidth}
                    height={plotHeight}
                    transform={`translate(${offsetX}, ${offsetY})`}
                    onMouseMove={e => {
                        if (this.state.isMouseOver) {
                            this.setState({ clientX: e.clientX, clientY: e.clientY });
                        }
                    }}
                >
                    {this.props.metrics
                        .filter(l => this.state.displayedChart === l.metric)
                        .map(l => this.renderMetric(l.metric, l.color))}
                </g>
            </AutoScaleSvg>
        );
    }

    private renderMetric(metric: string, color: string) {
        const { data, dateScale, valueScale } = this.buildData(metric);

        const line = d3
            .line<{ date: Date; value: number }>()
            .curve(d3.curveCardinal.tension(0))
            .x(v => dateScale(v.date))
            .y(v => valueScale(v.value));

        return (
            <path
                key={metric}
                d={line(data.filter(v => v.metric === metric))}
                fill="none"
                stroke={color}
                strokeWidth="2em"
                onMouseEnter={_ => this.setState({ isMouseOver: true, metric: metric })}
                onMouseOut={_ => this.setState({ isMouseOver: false, metric: undefined })}
            />
        );
    }

    private renderAxis(metric: string) {
        const element = createElement("g");
        const g = d3.select(element);

        const { data, dateScale, valueScale } = this.buildData(metric);

        const leftAxis = d3
            .axisLeft(valueScale)
            .tickFormat((v: number) => `${formatNumber(v)} per day`)
            .ticks(numOfTicks)
            .tickSizeOuter(0)
            .tickSizeInner(-plotWidth);

        g.append("g")
            .attr("class", "left-axis")
            .call(leftAxis as any);

        const bottomAxis = d3
            .axisBottom(dateScale)
            .tickValues(data.map(d => d.date))
            .tickFormat((v: Date) => d3.timeFormat("%b %d")(v))
            .tickSizeOuter(0)
            .tickSizeInner(-viewportHeight);

        g.append("g")
            .attr("class", "bottom-axis")
            .attr("transform", `translate(0, ${plotHeight + 2})`)
            .call(bottomAxis as any);

        return element.toReact();
    }

    private buildData(metric: string) {
        const data = this.props.chart.metrics
            .map(d =>
                d.data.map(v => ({
                    metric: d.metric,
                    date: new Date(v["dimension-values"][0]),
                    value: v["metric-value"]
                }))
            )
            .reduce((res, a) => [...res, ...a], [])
            .filter(d => d.metric === metric);

        const dateScale = d3
            .scaleTime()
            .domain(d3.extent(data, a => a.date))
            .range([0, plotWidth]);

        const valueDomain = d3.extent(data, a => a.value).reverse();

        valueDomain[0] = Math.max(valueDomain[0], 1);

        const valueScale = d3
            .scaleLinear()
            .domain(valueDomain)
            .range([0, plotHeight])
            .nice(numOfTicks);

        return { data, dateScale, valueScale };
    }

    private getTooltipInfo(
        metric: string,
        clientX: number,
        clientY: number
    ): {
        metric: string;
        date: Date;
        value: number;

        x: number;
        y: number;
    } {
        const { data, dateScale, valueScale } = this.buildData(metric);

        const { x } = clientCoordsToSvgCoords(this.svg, clientX, clientY);

        const approxValue = dateScale.invert(x - offsetX);
        const elem = elementWithMin(data, d => Math.abs(d.date.valueOf() - approxValue.valueOf()));

        return {
            ...elem,
            x: dateScale(elem.date) + offsetX,
            y: valueScale(elem.value) + offsetY
        };
    }

    private renderTooltip() {
        if (this.state.isMouseOver) {
            const { valueScale } = this.buildData(this.state.metric);

            const info = this.getTooltipInfo(this.state.metric, this.state.clientX, this.state.clientY);

            const chartLine = this.props.metrics.find(l => l.metric === this.state.metric);
            return (
                <>
                    <circle r="2.5em" cx={info.x} cy={info.y} fill={chartLine.color} />
                    <circle r="4em" cx={info.x} cy={info.y} fill="none" stroke={chartLine.color} strokeWidth="1em" />
                    <line
                        x1={info.x}
                        y1={valueScale(0) + offsetY}
                        x2={info.x}
                        y2={info.y}
                        stroke={chartLine.color}
                        strokeWidth="1em"
                        strokeDasharray="10em 3em"
                    />
                    <g transform={`translate(${info.x}, ${info.y})`}>
                        <g className="tooltip">
                            <rect className="tooltip__border" x="0" y="0" />
                            <g className="tooltip__g-metric">
                                <text className="tooltip__metric">{MetricLabels.metricSwitcher(chartLine)}</text>
                            </g>
                            <g className="tooltip__g-value">
                                <text className="tooltip__value">{info.value.toFixed(0)}</text>
                            </g>
                        </g>
                    </g>
                </>
            );
        } else {
            return undefined;
        }
    }

    private renderLegend() {
        return (
            <div className="legend">
                {this.props.metrics.map(d => (
                    <LegendItem
                        key={d.metric}
                        color={d.color}
                        checked={this.state.displayedChart === d.metric}
                        onChange={() =>
                            this.setState({
                                displayedChart: d.metric
                            })
                        }
                    >
                        {MetricLabels.metricSwitcher(d)}
                    </LegendItem>
                ))}
            </div>
        );
    }
}

export const ReportMetricsPerDateChart = connect(
    (state: AppState): ReportMetricsPerDateChartProps => {
        const campaign = currentReportCampaign(state);

        const metrics = campaignMetrics(campaign ? campaign.unit : "CPM").filter(m => !m.isRate);
        let chartData: any = FetchingState.getDataOrDefault(state.reports.chartData.dateData, {
            metrics: [],
            dimensions: []
        });

        chartData = filterChartData(chartData, campaign);

        return {
            chart: chartData,
            metrics
        };
    }
)(ReportMetricsPerDateChartComponent);

function filterChartData(chartData: any, campaign: ReportCampaignListItem | undefined) {
    if (!campaign || !chartData) {
        return {};
    }

    return {
        ...chartData,
        metrics: chartData.metrics.map((m: any) => ({
            ...m,
            data: m.data.filter((d: any) =>
                checkIsDateInCampaign(campaign.startDate, campaign.endDate, d["dimension-values"][0])
            )
        }))
    };
}

function checkIsDateInCampaign(startDate: string, endDate: string, date: string): boolean {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const value = new Date(date);

    return value >= start && value <= end;
}
